import { useMemo, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import {
  painelButtonOutlineClass,
  painelButtonPrimaryClass,
  painelDialogSurfaceClass,
} from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';

function parseYmd(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const d = parseISO(trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed);
  return isValid(d) ? d : undefined;
}

function toYmd(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

type PainelDatePickerFieldProps = {
  id?: string;
  label: string;
  value: string;
  onChange: (ymd: string) => void;
  className?: string;
  disabled?: boolean;
  hint?: string;
};

/** Campo de data do painel com modal de calendário no tema escuro. */
export function PainelDatePickerField({
  id,
  label,
  value,
  onChange,
  className,
  disabled,
  hint,
}: PainelDatePickerFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseYmd(value), [value]);
  const [draft, setDraft] = useState<Date | undefined>(selected);

  const display = selected
    ? format(selected, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
    : 'Escolher data';

  const openPicker = () => {
    if (disabled) return;
    setDraft(selected ?? new Date());
    setOpen(true);
  };

  const confirm = () => {
    if (draft) onChange(toYmd(draft));
    setOpen(false);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id} className="font-poppins text-slate-300">
        {label}
      </Label>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={openPicker}
        className={cn(
          painelInputClass,
          'flex h-10 w-full items-center justify-between gap-2 text-left',
          !selected && 'text-slate-500',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <span className="min-w-0 truncate font-inter text-sm">
          {selected ? (
            <span className="text-blue-50">{display}</span>
          ) : (
            display
          )}
        </span>
        <CalendarIcon className="h-4 w-4 shrink-0 text-sky-300" aria-hidden />
      </button>
      {hint ? <p className="font-inter text-xs text-slate-500">{hint}</p> : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className={cn(
            painelDialogSurfaceClass,
            'max-w-[min(100vw-2rem,22rem)] gap-0 overflow-hidden p-0 sm:rounded-2xl'
          )}
        >
          <DialogHeader className="border-b border-white/10 px-5 py-4 text-left">
            <DialogTitle className="font-poppins text-base font-semibold text-white">
              {label}
            </DialogTitle>
            <DialogDescription className="font-inter text-sm text-slate-400">
              {draft
                ? format(draft, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })
                : 'Selecione um dia no calendário'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-center px-3 py-4">
            <Calendar
              mode="single"
              selected={draft}
              onSelect={(d) => d && setDraft(d)}
              locale={ptBR}
              defaultMonth={draft ?? selected ?? new Date()}
              className="rounded-xl bg-transparent text-slate-100"
              classNames={{
                months: 'flex flex-col space-y-3',
                month: 'space-y-3',
                caption: 'flex justify-center relative items-center pt-1',
                caption_label: 'font-poppins text-sm font-semibold capitalize text-sky-100',
                nav: 'flex items-center gap-1',
                nav_button: cn(
                  'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10',
                  'bg-white/[0.04] text-sky-200 opacity-100 transition-colors',
                  'hover:border-sky-400/35 hover:bg-sky-500/15 hover:text-white'
                ),
                nav_button_previous: 'absolute left-1',
                nav_button_next: 'absolute right-1',
                table: 'w-full border-collapse',
                head_row: 'flex',
                head_cell:
                  'w-9 rounded-md font-poppins text-[0.7rem] font-medium uppercase tracking-wide text-slate-500',
                row: 'mt-1.5 flex w-full',
                cell: 'relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20',
                day: cn(
                  'inline-flex h-9 w-9 items-center justify-center rounded-lg font-inter text-sm',
                  'text-slate-200 transition-colors hover:bg-sky-500/20 hover:text-white',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40'
                ),
                day_selected:
                  'bg-sky-500 text-white hover:bg-sky-400 hover:text-white focus:bg-sky-500 focus:text-white',
                day_today: 'bg-white/10 text-sky-100 ring-1 ring-sky-400/40',
                day_outside: 'text-slate-600 opacity-60 aria-selected:bg-sky-500/40 aria-selected:text-white',
                day_disabled: 'text-slate-600 opacity-40',
                day_hidden: 'invisible',
              }}
            />
          </div>

          <DialogFooter className="flex-row justify-between gap-2 border-t border-white/10 bg-white/[0.03] px-4 py-3 sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className={cn(painelButtonOutlineClass, 'h-9 px-3')}
              onClick={() => {
                const today = new Date();
                setDraft(today);
                onChange(toYmd(today));
                setOpen(false);
              }}
            >
              Hoje
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className={cn(painelButtonOutlineClass, 'h-9 px-3')}
                onClick={() => setOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className={cn(painelButtonPrimaryClass, 'h-9 px-4')}
                disabled={!draft}
                onClick={confirm}
              >
                Confirmar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
